/*
  ctr-rum.js - real user monitoring for Core Web Vitals, per template.
  Spec: RUM-SPEC-31AUG.md. Built 31 Aug 2026.

  WHY THEME-SIDE AND NOT GTM. gtm.js loads at 458 ms AND is gated on consent, so a
  GTM-delivered listener does not exist when the consent banner is clicked - and that click is
  the only interaction ever measured on this storefront as a plausible INP driver (208 ms
  median on Slow 4G / 4x CPU, 352 ms cold). GTM-side RUM is blind to the prime suspect for
  every visitor, always. That is a blind spot at the point of interest, not a sampling bias.

  WHY IT DOES NOT GATE THE SEND. Consent Mode is ADVANCED (confirmed 31 Aug), so gtag loads
  before consent and sends cookieless pings when consent is denied. The denied cohort is
  captured rather than dropped, so there is nothing to buffer and nothing to flush.

  NOTHING IS STORED ON THE DEVICE. No cookie, no localStorage, no sessionStorage, no
  identifier of our own. Grouping is by template, which is the unit of analysis anyway.

  REPORTING IS BFCACHE-SAFE. web-vitals reports on visibilitychange->hidden and pagehide
  internally and never touches beforeunload, which would disable bfcache and make this file a
  performance defect in its own right. Do not add a beforeunload listener.
*/

const cfg = document.getElementById('ctr-rum');
if (cfg) {
  const TEMPLATE = cfg.dataset.template || 'unknown';
  const LIB = cfg.dataset.lib;

  /* Signed-in state, stamped by Liquid from `customer`, NOT sniffed from a cookie.
     Two values only, "1" and "0", so it stays a clean low-cardinality dimension and
     carries nothing that identifies a person.

     WHY IT IS HERE. On 1 Sep 2026 the homepage Yotpo loader was split so that signed-out
     visitors do not download 430 KB of decoded JavaScript they cannot see, while signed-in
     customers keep the points banner. Charlie's question was the right one: field scores
     - CrUX, PageSpeed - include signed-in visitors too, so the p75 only moves if signed-out
     sessions are the large majority. Shopify's analytics exposes no signed-in dimension and
     cannot answer it. This can: split any metric by wv_signed_in and read the real share.
     GA4 does NOT backfill, so this measures from the day it ships, never before. */
  const SIGNED_IN = cfg.dataset.signedIn === '1' ? '1' : '0';
  // wv_cart (6 Sep 2026): "1" when the cart has at least one line, else "0". A non-empty
  // cart bypasses Shopify's full-page cache on EVERY page for as long as the cart cookie
  // lives - including a returning visitor's first page - so it is the one cold-render
  // population this script could not see. TTFB-DECISIONS-05Sep2026.md estimates it at
  // 10-20% of loads against signed-in at 3-4%. Two values, no identifier, same reasoning
  // as wv_signed_in. GA4 dimension "WV Cart" (event scope, param wv_cart) exists since
  // 6 Sep 2026 on p283073031; GA4 does not backfill, data starts when this ships.
  const CART = cfg.dataset.cart === '1' ? '1' : '0';

  /* GA4 dimension cardinality degrades past roughly 500 distinct values a day and silently
     buckets the rest into "(other)" - which would destroy the one dimension that answers
     "which element". Raw selectors are unbounded: "#product-42 > div:nth-child(7) > button".
     Strip positional indices, collapse digit runs, keep the last 3 segments only. A stable
     low-cardinality selector is worth far more than a precise one that gets bucketed away. */
  const sel = (s) => {
    if (!s) return '';
    return String(s)
      .replace(/:nth-(child|of-type)\(\d+\)/g, '')
      .replace(/\d{2,}/g, '#')
      .split(/\s*>\s*/).slice(-3).join('>')
      .trim().slice(0, 100);
  };

  const url = (u) => {
    if (!u) return '';
    try { const p = new URL(u); return (p.origin + p.pathname).slice(0, 100); }
    catch { return String(u).slice(0, 100); }
  };

  const num = (v) => (typeof v === 'number' && isFinite(v) ? Math.round(v) : 0);

  /* The LoAF script that dominated the frame. This is the dimension that names
     pandectes-core-latest.js or a ctr-* file outright, which is the whole point of using the
     attribution build rather than the standard one. */
  const loaf = (a) => {
    const frames = a.longAnimationFrameEntries || [];
    let best = null;
    for (const f of frames) for (const s of (f.scripts || [])) {
      if (!best || s.duration > best.duration) best = s;
    }
    return best ? url(best.sourceURL) : '';
  };

  /* Document timing for the four slots that were always 0 (R-RUM-SRV, 15 Sep 2026).
     LCP d1 and FCP d1 read 0 on every row from 1 to 12 Sep because `parts()` asked for
     `a.ttfb`, a property the attribution build does not have - it names it `timeToFirstByte`
     (found by Codex round RUM-SRV, 15 Sep). FCP d3 / CLS d3 were hard-coded 0. (Even the right
     property would have read responseStart, which on this store is the 103 Early Hints.) Signed-in loads paint ~300 ms later than signed-out, entirely AFTER the
     hints (FCP firstByteToFCP 930 vs 606 ms p50, mobile), and no field could say whether that
     is Shopify building the page, the document streaming, or a heavier document. These can:
       serverProcessing   Shopify's own Server-Timing `processing` duration for this document
       documentStart      the REAL response headers (finalResponseHeadersStart, Chrome 133+);
                          falls back to responseStart where unsupported (Safari has no 103 split)
       documentEnd        responseEnd, the last byte of the HTML
       documentKb         encodedBodySize of the HTML, in KB
     Only a `navigate` document has these; a bfcache restore or prerender keeps 0. */
  const doc = (() => {
    try {
      const n = performance.getEntriesByType('navigation')[0];
      if (!n) return null;
      const st = (n.serverTiming || []).find((t) => t.name === 'processing');
      const start = typeof n.finalResponseHeadersStart === 'number' && n.finalResponseHeadersStart > 0
        ? n.finalResponseHeadersStart : n.responseStart;
      return { processing: st ? num(st.duration) : 0, start: num(start), end: num(n.responseEnd), kb: num((n.encodedBodySize || 0) / 1000) };
    } catch { return null; }
  })();
  const docFor = (m) => (doc && m.navigationType === 'navigate' ? doc : { processing: 0, start: 0, end: 0, kb: 0 });

  /* Sub-part mapping. FIXED - it is documented in RUM-SPEC-31AUG.md section 2 and the
     BigQuery queries in section 9 depend on it. Do not reorder these.
     R-RUM-SRV (15 Sep 2026) filled only the slots that were provably always 0 - LCP d1,
     FCP d1, FCP d3, CLS d3. Every other slot keeps its meaning, so older rows still compare. */
  const parts = (name, a, m) => {
    const d = docFor(m);
    switch (name) {
      case 'INP':  return [num(a.inputDelay), num(a.processingDuration), num(a.presentationDelay)];
      case 'LCP':  return [d.end, num(a.resourceLoadDelay), num(a.elementRenderDelay)];
      case 'CLS':  return [Math.round((a.largestShiftValue || 0) * 1e4), num(a.largestShiftTime), d.kb];
      case 'TTFB': return [num(a.waitingDuration), num(a.dnsDuration) + num(a.connectionDuration), num(a.requestDuration)];
      case 'FCP':  return [d.start, num(a.firstByteToFCP), d.processing];
      default:     return [0, 0, 0];
    }
  };

  const target = (name, a) => {
    if (name === 'INP') return a.interactionTarget;
    if (name === 'CLS') return a.largestShiftTarget;
    return a.target || a.element;
  };

  const send = (m) => {
    const a = m.attribution || {};
    const [d1, d2, d3] = parts(m.name, a, m);
    const p = {
      wv_metric: m.name,
      wv_rating: m.rating,
      wv_id: m.id,                                   // dedup. INP and CLS report repeatedly.
      wv_template: TEMPLATE,                         // from Liquid, never a URL regex.
      wv_signed_in: SIGNED_IN,                       // "1"/"0" from Liquid. See above.
      wv_cart: CART,                                 // "1"/"0" from Liquid. See above.
      wv_target: sel(target(m.name, a)),
      wv_loaf_script: loaf(a),
      wv_nav_type: m.navigationType || '',           // separates bfcache restores.
      wv_load_state: a.loadState || '',
      wv_interaction: a.interactionType || '',
      wv_theme_id: String((window.Shopify && Shopify.theme && Shopify.theme.id) || ''),
      wv_consent: consent(),
      wv_value: m.name === 'CLS' ? Math.round(m.value * 1e4) / 1e4 : num(m.value),
      wv_d1: d1, wv_d2: d2, wv_d3: d3
    };

    /* ALWAYS a plain dataLayer object, NEVER gtag(). GA4 is configured through GTM, whose
       "web_vitals" tag reads every wv_* field as a Data Layer Variable - and a DLV cannot read
       the parameters of a gtag('event', ...) call, which lands as an arguments object.

       R-RUM-GTAG, 14 Sep 2026. From 12 Sep ~12:00 UTC a Shopify web pixel
       (web-pixel-2205155443) started defining window.gtag at ~300 ms on every page. This line
       used to PREFER gtag when it existed, so from that hour 63% of web_vitals events on
       13 Sep reached GA4 with no wv_* parameters at all, and the rest were stale repeats of an
       earlier metric (one FCP wv_id sent 24 times). BigQuery rows before 12 Sep 12:00 UTC are
       clean; rows after it are not. Working: ITEM78-RESULTS-14Sep2026.md. */
    (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'web_vitals' }, p));
  };

  /* A DIMENSION rather than a gate - it exists so we can TEST whether the denied cohort is
     slower instead of assuming it.

     CORRECTED 31 Aug. The first version called google_tag_data.ics.getConsentState(), which
     returned nothing on live and left every event reading "unknown" - verified in GA4
     DebugView. That object exists but its shape is internal and undocumented, so relying on
     one method of it was the mistake.

     Primary route is now the dataLayer itself, which is a PUBLIC contract: Consent Mode
     pushes gtag('consent', 'default'|'update', {...}) and those land as arguments objects.
     Reading the LAST analytics_storage value gives the state after Pandectes has updated it.
     The ics object is kept only as a fallback, read defensively by property rather than by
     calling a method that may not exist. */
  function consent() {
    try {
      const dl = window.dataLayer || [];
      let state = null;
      for (let i = 0; i < dl.length; i++) {
        const e = dl[i];
        if (e && e[0] === 'consent' && e[2] && typeof e[2] === 'object'
            && 'analytics_storage' in e[2]) {
          state = e[2].analytics_storage;      // last one wins: default, then update
        }
      }
      if (state === 'granted' || state === 'denied') return state;
    } catch { /* fall through to the fallback */ }

    try {
      const ics = window.google_tag_data && window.google_tag_data.ics;
      const entry = ics && ics.entries && ics.entries.analytics_storage;
      if (entry) {
        const v = entry.update !== undefined ? entry.update : entry.default;
        if (v === true) return 'granted';
        if (v === false) return 'denied';
      }
    } catch { /* unknown is an acceptable value */ }

    return 'unknown';
  }

  /* ---------------------------------------------------------------------------------------
     NON-200 RESOURCE LOGGING. Requested by Cowork in BRIEF-RUM-NON-200-LOGGING.md, 1 Sep 2026,
     after Shopify ticket 69966526 asked for a HAR containing a genuine 503 and 195 hand-probed
     requests all returned 200. There is no error to catch by hand; instrument for it instead.

     PerformanceResourceTiming.responseStatus carries the HTTP status of a completed fetch and
     works cross-origin, so a PerformanceObserver on resource entries is the whole mechanism.

     WHAT THIS DELIBERATELY DOES NOT DO. It never re-requests a failing URL to read its headers.
     Cf-Ray is not in resource timing and is only readable same-origin via fetch(), and retrying
     against an origin that is already failing is how a small edge blip becomes an outage. Log
     the URL, status and time; pull the ray from x-request-id server-side if this ever fires.

     responseStatus === 0 is reported separately as status 0. It is an opaque or blocked
     response, not a success, and consent tooling produces them - so it is worth counting but
     must not be read as a server fault.

     RATE LIMITED HARD, WITH TWO SEPARATE BUDGETS - and the second budget exists because the
     first version of this failed its own acceptance test.

     Cowork's brief specified one cap of 5. Verified on dev 1 Sep 2026, a NORMAL collection page
     load produced five events and every one was status 0:

         0  early-hints  /extensions/.../keepcart-2-248/assets/app.css
         0  script       /shopifycloud/preview-bar/preview-bar-modules.js
         0  script       /extensions/.../gdpr-cookie-consent-399/assets/pandectes-rules-late...
         0  script       /extensions/.../gdpr-cookie-consent-399/assets/pandectes-core-lates...
         0  script       /extensions/.../keepcart-2-248/assets/vars.generated.js

     A deliberate 404 fired straight after was then DROPPED, because the cap was already spent.
     Opaque responses are common and uninteresting; a 5xx is rare and is the entire reason this
     exists. One shared budget lets the common case starve the rare one.

     So: up to 5 events for status >= 400, and up to 2 for status 0, counted independently.
     De-duplicated by url+status either way. A page that fails 60 subresources still sends at
     most 7 beacons.

     Same transport as web_vitals, and the same consent reasoning - no gate, because Consent
     Mode is Advanced. A GTM tag mapping the event name rum_resource_error is required for this
     to reach GA4, exactly as web_vitals needs one. */
  const ERR_MAX = 5;        // status >= 400: the ones we are actually hunting
  const OPAQUE_MAX = 2;     // status 0: worth counting, must never crowd out the above
  const seenErrors = new Set();
  let errorsSent = 0;
  let opaqueSent = 0;

  function reportResourceError(entry) {
    const status = entry.responseStatus;
    if (status === undefined || status === null) return;      // browser has no responseStatus
    if (status !== 0 && status < 400) return;                 // a real success

    const opaque = status === 0;
    if (opaque ? opaqueSent >= OPAQUE_MAX : errorsSent >= ERR_MAX) return;

    let url, host;
    try {
      const u = new URL(entry.name, location.href);
      url = (u.pathname + u.search).slice(0, 100);
      host = u.hostname;
    } catch {
      url = String(entry.name).slice(0, 100);
      host = '';
    }

    const key = url + '|' + status;
    if (seenErrors.has(key)) return;
    seenErrors.add(key);
    if (opaque) opaqueSent++; else errorsSent++;

    const p = {
      rum_url: url,                                  // pathname + search only. No identifiers.
      rum_host: host,                                // WHICH host failed. Added after the first
                                                     // real catch reported a 503 on "/v1/metrics"
                                                     // and nothing said whose /v1/metrics it was.
                                                     // A hostname is not a customer identifier.
      rum_status: status,
      rum_initiator: entry.initiatorType || '',
      rum_page: location.pathname,
      rum_template: TEMPLATE,
      rum_signed_in: SIGNED_IN
    };

    pending.push(p);
  }

  /* THE SEND IS DEFERRED, AND THAT IS NOT A STYLE CHOICE - the first version lost every event.

     Verified on live 1 Sep 2026 after publishing GTM version 75: the events reached
     window.dataLayer correctly, the tag and trigger were bound correctly, and NOT ONE reached
     GA4. A manual dataLayer.push of the same event, made by hand once the page had settled,
     fired the tag immediately and produced a /g/collect carrying every parameter. So the
     configuration was never wrong - the timing was.

     `buffered: true` hands us the backlog the instant the observer is created, which is inside
     a deferred module in <head>. gtm.js loads later still, because Pandectes gates it, and it
     did not replay those pushes. web_vitals never hit this because the web-vitals library
     reports on visibilitychange, long after GTM is up.

     So resource errors are queued and flushed on the same signal: visibilitychange to hidden,
     pagehide, or a 12 s ceiling for a tab that is simply left open. Never beforeunload - that
     disables bfcache and would make this a performance defect in its own right. */
  const pending = [];
  let flushWaits = 0;

  /* AND THE FLUSH WAITS FOR GTM, NOT FOR A MOMENT IN TIME. Deferring to visibilitychange was
     still not enough: a tab that is ALREADY hidden when it loads - a background tab, or any
     page opened behind another - fires visibilitychange immediately, flushes at once, and lands
     in the same gap before gtm.js exists. Observed directly while verifying this, where every
     page in the harness runs hidden.

     So the trigger says WHEN to try and this check says WHETHER we can. If the transport is not
     up yet the queue is kept and retried once a second for 30 s. Nothing is dropped for being
     early, and a page closed before GTM loads simply loses the event, which is no worse than
     the behaviour it replaces. */
  function transportReady() {
    /* GTM itself, not window.gtag: a Shopify web pixel defines gtag long before gtm.js
       exists (R-RUM-GTAG, see the web_vitals send above). */
    const gtm = window.google_tag_manager;
    return !!(gtm && Object.keys(gtm).length);
  }

  function flush() {
    if (!pending.length) return;
    if (!transportReady()) {
      if (flushWaits++ < 30) setTimeout(flush, 1000);
      return;
    }
    const batch = pending.splice(0);
    for (let i = 0; i < batch.length; i++) {
      /* Object push only, for the same reason as web_vitals (R-RUM-GTAG). */
      (window.dataLayer = window.dataLayer || []).push(
        Object.assign({ event: 'rum_resource_error' }, batch[i])
      );
    }
  }

  addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
  addEventListener('pagehide', flush);
  setTimeout(flush, 12000);

  try {
    const ro = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      for (let i = 0; i < entries.length; i++) reportResourceError(entries[i]);
    });
    /* buffered:true replays entries that completed before this script ran, which on a deferred
       module is most of the page. Without it the early failures - the ones that matter - are
       invisible. */
    ro.observe({ type: 'resource', buffered: true });
  } catch { /* a diagnostic must never break the page */ }

  import(LIB).then(({ onCLS, onINP, onLCP, onFCP, onTTFB }) => {
    onCLS(send); onINP(send); onLCP(send); onFCP(send); onTTFB(send);
  }).catch(() => { /* a diagnostic must never break the page */ });
}
